import { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Avatar,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import {
  QrCode,
  Email,
  WhatsApp,
  FacebookOutlined,
  Close,
} from "@mui/icons-material";
import QRCode from "react-qr-code";
import { IThemeConfig } from "./types/memorial-types";
import { Copy } from "lucide-react";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../../components/common/ShowToast";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import apiService from "../../../../../service/Api";

interface ShareControlsProps {
  theme: IThemeConfig;
  streamData: {
    id: number;
    website_stream_link?: string;
    title?: string;
    booking?: {
      id: number;
      service: string;
      deceased?: Array<{
        id: number;
        person_image?: string;
      }>;
    };
  } | null;
  donationsEnabled?: boolean;
  streamProgram?: string | null;
  downloadingProgram?: boolean;
  handleDownloadProgram?: () => void;
}

export default function ShareControls({
  streamData,
  theme,
  donationsEnabled = false,
  streamProgram,
  handleDownloadProgram,
  downloadingProgram,
}: ShareControlsProps) {
  const [openQr, setOpenQr] = useState(false);
  const [openDonationDialog, setOpenDonationDialog] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(10);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [memory, setMemory] = useState("");

  const [currency, setCurrency] = useState<string>("USD");
  // const [donorEmail, setDonorEmail] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleWhatsAppShare = () => {
    if (!streamData?.website_stream_link) return;

    const messageTemplate = `It is with great sadness that we share the livestream link for the funeral ${streamData.title}.

You can view it here: ${streamData.website_stream_link}`;

    const text = encodeURIComponent(messageTemplate);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleEmailShare = () => {
    if (!streamData?.website_stream_link) return;

    const subject = encodeURIComponent(
      `Funeral Service for ${streamData.title} - Livestream Link`,
    );
    const body = encodeURIComponent(
      `Dear friends and family,

We are sharing the livestream link for the funeral service of ${streamData.title}

You can view the livestream here: ${streamData.website_stream_link}

Thank you for your thoughts and support during this difficult time.

Sincerely,`,
    );

    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleCopyUrl = async () => {
    if (!streamData?.website_stream_link) return;
    try {
      await navigator.clipboard.writeText(streamData.website_stream_link);
      showSuccessToast("Link Copied Succesfully ");
    } catch (err) {
      console.error("Failed to copy URL: ", err);
    }
  };

  const handleOpenDonationDialog = () => {
    if (!donationsEnabled) return;
    setOpenDonationDialog(true);
  };

  const handleCloseDonationDialog = () => {
    setOpenDonationDialog(false);
  };

  const handleProceedWithPayment = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a valid donation amount");
      return;
    }

    if (!streamData?.booking?.id) {
      showErrorToast("Booking information is missing");
      return;
    }

    // if (!donorEmail || !donorEmail.includes("@")) {
    //   showErrorToast("Please enter a valid email address");
    //   return;
    // }

    setIsProcessing(true);
    try {
      const response = await apiService.post<{ redirect_url: string }>(
        "/api/chema/donations/initiate/",
        {
          amount: selectedAmount,
          booking_id: streamData.booking.id,
          currency: currency,
          // email: donorEmail,
        },
      );

      if (response.data?.redirect_url) {
        // Redirect to PayNow payment URL
        window.location.href = response.data.redirect_url;
      } else {
        showErrorToast("Payment initialization failed. Please try again.");
        setIsProcessing(false);
      }
    } catch (error: unknown) {
      console.error("Donation initiation error:", error);
      const errorMessage =
        error instanceof Object &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response &&
        typeof error.response.data === "object" &&
        error.response.data !== null &&
        "message" in error.response.data
          ? ((error.response.data as Record<string, unknown>).message as string)
          : "Failed to initiate payment. Please try again.";
      showErrorToast(errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: "60px",
            py: 1,
            px: 2,
            boxShadow: 0,
            textTransform: "none",
            opacity: streamProgram ? 1 : 0.5,
          }}
          variant="contained"
          onClick={handleDownloadProgram}
          disabled={!streamProgram || downloadingProgram}
        >
          {downloadingProgram ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            <>
              <SimCardDownloadOutlinedIcon sx={{ color: "#fff", mr: 1 }} />
              <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                Download Program
              </Typography>
            </>
          )}
        </Button>

        {streamData?.booking?.service !== "Conference" && (
          <Button
            onClick={handleOpenDonationDialog}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: "60px",
              py: 1,
              px: 2,
              boxShadow: 0,
            }}
            variant="contained"
            disabled={!donationsEnabled}
          >
            <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
              Donate Chema
            </Typography>
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
        }}
      >
        {/* WhatsApp */}
        <IconButton
          sx={{
            backgroundColor: "#25D366",
            color: theme.surface,
            "&:hover": { backgroundColor: "#1DA851" },
            mr: 1,
          }}
          onClick={handleWhatsAppShare}
        >
          <WhatsApp />
        </IconButton>
        {/*Facebook*/}
        <IconButton
          sx={{
            backgroundColor: "info.main",
            color: theme.surface,
            "&:hover": { backgroundColor: "info.light" },
            mr: 1,
          }}
        >
          <FacebookOutlined />
        </IconButton>

        {/* Email */}
        <IconButton
          sx={{
            backgroundColor: "#EA4335",
            color: theme.surface,
            "&:hover": { backgroundColor: "#D63A2C" },
            mr: 1,
          }}
          onClick={handleEmailShare}
        >
          <Email />
        </IconButton>

        {/* QR Code */}
        <IconButton
          sx={{
            backgroundColor: "#ffffff",
            color: "#000",
            mr: 1,
          }}
          onClick={() => setOpenQr(true)}
        >
          <QrCode />
        </IconButton>

        {/* Groups / Share */}
        <IconButton
          onClick={handleCopyUrl}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            "&:hover": { backgroundColor: "#807d7dff" },
          }}
        >
          <Copy />
        </IconButton>
      </Box>

      {/* QR Dialog */}
      <Dialog open={openQr} onClose={() => setOpenQr(false)}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <QRCode value={streamData?.website_stream_link || ""} size={200} />
        </DialogContent>
      </Dialog>

      {/* Donation / Chema Dialog */}
      <Dialog
        open={openDonationDialog}
        onClose={handleCloseDonationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={streamData?.booking?.deceased?.[0]?.person_image || ""}
                sx={{ width: 56, height: 56 }}
              >
                {streamData?.title ? streamData.title.charAt(0) : "C"}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">
                  Your contribution helps{" "}
                  <Typography component="span" fontWeight="bold">
                    {streamData?.title?.replace("Stream for ", "") || "The"} Family
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Helping today builds stronger ties for tomorrow.
                </Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={handleCloseDonationDialog}
              size="small"
              sx={{ ml: "auto" }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            Enter Your Donation
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Donation Amount"
              value={selectedAmount ?? ""}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, "");
                setSelectedAmount(value.length > 0 ? parseFloat(value) : null);
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography>$</Typography>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="currency-label">Select Currency</InputLabel>
              <Select
                labelId="currency-label"
                id="currency-select"
                value={currency}
                label="Select Currency"
                onChange={(e) => setCurrency(e.target.value)}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZWL">ZWL</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            mt={3}
            flexWrap="wrap"
          >
            {[10, 20, 30, 50, 60].map((amount) => (
              <Button
                key={amount}
                variant={selectedAmount === amount ? "contained" : "outlined"}
                onClick={() => setSelectedAmount(amount)}
                sx={{
                  flex: "1 1 80px",
                  m: 0.5,
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                ${amount}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Donate as Anonymous (Not show name on feed)
              </Typography>
            }
          />

          <TextField
            label={`Leave a memory on ${streamData?.title || "the deceased"}`}
            multiline
            minRows={3}
            fullWidth
            value={memory}
            onChange={(e) => setMemory(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleProceedWithPayment}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isProcessing}
            sx={{ borderRadius: "30px", textTransform: "none", py: 1.2 }}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                Processing...
              </>
            ) : (
              "Proceed with payment"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
